// Chiffrage proposé depuis le CCTP, le DPGF et les métrés : écran de revue. Pour chaque ligne du DPGF (ou du bordereau) et chaque
// exigence du CCTP sans ligne au DPGF, l'ouvrage type proposé (règles déclaratives, aucune IA), la quantité AVEC SA SOURCE, l'unité,
// la confiance expliquée en clair, l'écart CCTP ↔ DPGF s'il y en a un, et — pour les rôles qui voient les coûts — les heures et le
// matériel qui en découlent (prix du catalogue daté et son ancienneté, sinon « prix à saisir »).
// La personne coche, change l'ouvrage, corrige la quantité, puis « Ajouter au devis » (barre collante, visible sans défiler).
// Rien n'est ajouté avant ce clic ; rejouer l'ajout n'ajoute rien (élément déjà au devis).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ListPlus, Lock, Sparkles } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, euros, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, EtatErreur, EtatVide, LienBouton, MarqueIa, Panneau, Source, useNotifier } from '../../components/ui.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { LIBELLES_SECTIONS, quantite, saisieValide, type Section } from './commun.tsx';

interface SourceDoc { titre: string; versionId: string | null; documentId: string | null; page: number | null; locator: string | null; excerpt: string | null }
interface OuvrageResume { id: string; code: string; label: string; unit: string }
interface Item {
  cle: string; origine: 'cadre' | 'exigence' | 'comptage'; libelle: string;
  cadre: { ref: string | null; designation: string; quantite: number | null; unite: string | null; piece: string; docType: string; source: SourceDoc } | null;
  exigence: { id: string; label: string; detection: string; nature: string; status: string; source: SourceDoc | null } | null;
  exigencesCouvertes: { id: string; label: string; detection: string }[];
  regle: { code: string; objet: string } | null;
  ouvrage: OuvrageResume | null; alternatives: (OuvrageResume & { objet: string })[]; absence: string | null;
  quantite: string | null; unite: string | null;
  sourceQuantite: { type: 'dpgf' | 'bordereau' | 'comptage_plan' | 'cctp' | 'metre' | 'aucune'; libelle: string; source?: SourceDoc | null };
  suggestions: { quantite: string; unite: string; libelle: string }[];
  confiance: 'forte' | 'moyenne' | 'faible'; explication: string; ecart: { message: string } | null;
  deja: boolean; avertissements: string[]; coche: boolean;
}
interface Tarif { etat: 'sans_reference' | 'introuvable' | 'sans_prix' | 'prix'; prix?: string; date?: string | null; age_mois?: number | null; ancien?: boolean; source?: string }
interface Composant {
  id: string; role: 'base' | 'penser_a_tout'; section: Section; label: string; unit: string; qty_per_unit: string; per_unit: boolean; fourniture: boolean;
  reference: string | null; heures?: string | null; tarif?: Tarif | null;
}
interface OuvrageCatalogue {
  id: string; code: string; label: string; unit: string; metier: 'cvc' | 'gtb' | 'elec' | null; categorie: string; estimation: boolean;
  heures?: { par_unite: string | null; fixes: string | null; a_renseigner: number }; origine_temps?: string; composants: Composant[];
}
interface Proposition {
  revision: { id: string; rev_no: number; variant: string; status: string };
  devis: { id: string; number: string; title: string };
  dossier: { id: string; reference: string; title: string };
  droits: { couts: boolean; ecrire: boolean; modifiable: boolean };
  pieces: { cadres: { titre: string; type: string; version: string | null }[]; comptages_cctp: number; metres: number; reperes_a_valider?: number };
  items: Item[]; catalogue: OuvrageCatalogue[]; seuil_mois: number | null; taux_horaires: { code: string; label: string }[];
  resume: { total: number; cadre: number; exigences: number; avec_ouvrage: number; quantite_a_saisir: number; ecarts: number; deja: number; coches: number; ia: number };
}
interface Choix { coche: boolean; ouvrageId: string; quantite: string; unite: string }

const METIERS: Record<string, string> = { cvc: 'CVC', gtb: 'GTB et domotique', elec: 'Électricité' };
const CONFIANCE: Record<Item['confiance'], { libelle: string; ton: 'succes' | 'ambre' | 'neutre' }> = {
  forte: { libelle: 'Confiance forte', ton: 'succes' }, moyenne: { libelle: 'À vérifier', ton: 'ambre' }, faible: { libelle: 'Sans ouvrage sûr', ton: 'neutre' },
};
/** Décimal serveur → saisie française (« 12.5 » → « 12,5 »). */
const versSaisie = (v: string | null) => (v === null ? '' : String(Number(v)).replace('.', ','));
const versNombre = (s: string) => (s.trim() === '' || !saisieValide(s) ? null : Number(s.replace(/[\s  ]/g, '').replace(',', '.')));
const h2 = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

function choixInitial(i: Item): Choix {
  return { coche: i.coche, ouvrageId: i.ouvrage?.id ?? '', quantite: versSaisie(i.quantite), unite: i.unite ?? '' };
}

export function PagePropositionChiffrage() {
  const { id = '', quoteId = '' } = useParams();
  const [params] = useSearchParams();
  const revisionId = params.get('revision') ?? '';
  const r = useQuery({ queryKey: ['proposition-chiffrage', revisionId], enabled: !!revisionId,
    queryFn: () => api.get<Proposition>(`/api/devis/revisions/${revisionId}/proposition`) });
  const retour = `/etudes/${id}/devis/${quoteId}${revisionId ? `?revision=${revisionId}` : ''}`;
  return (
    <DossierCadre dossierId={id} actif="devis" large
      actions={<LienBouton to={retour} icone={<ArrowLeft aria-hidden />}>Revenir au devis</LienBouton>}>
      {!revisionId && <EtatVide titre="Révision non précisée" texte="Ouvrez la proposition depuis l’éditeur du devis." />}
      {r.isLoading && <Chargement lignes={6} libelle="Lecture du CCTP, du DPGF et des métrés…" />}
      {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
      {r.data && <Revue p={r.data} retour={retour} />}
    </DossierCadre>
  );
}

function Revue({ p, retour }: { p: Proposition; retour: string }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [choix, setChoix] = useState<Record<string, Choix>>({});
  const [envoi, setEnvoi] = useState(false);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  // Taux horaire des heures des ouvrages ajoutés : le seul taux actif d'office ; plusieurs taux = choix explicite (jamais deviné).
  const [taux, setTaux] = useState(p.taux_horaires.length === 1 ? p.taux_horaires[0].code : '');
  const refs = useRef<Record<string, HTMLInputElement | null>>({});
  // Choix initiaux : ceux de la proposition ; un élément déjà vu garde la saisie en cours (rafraîchissement après ajout).
  useEffect(() => {
    setChoix((avant) => Object.fromEntries(p.items.map((i) => [i.cle, i.deja ? { ...choixInitial(i), coche: false } : avant[i.cle] ?? choixInitial(i)])));
  }, [p.items]);
  const catalogue = useMemo(() => new Map(p.catalogue.map((o) => [o.id, o])), [p.catalogue]);
  const modifiable = p.droits.modifiable;
  const lecture = !modifiable;
  const cadre = p.items.filter((i) => i.origine === 'cadre');
  const exigences = p.items.filter((i) => i.origine === 'exigence');
  const comptages = p.items.filter((i) => i.origine === 'comptage');
  const coches = p.items.filter((i) => !i.deja && choix[i.cle]?.coche);
  const sansQuantite = coches.filter((i) => versNombre(choix[i.cle].quantite) === null).length;
  const changer = (cle: string, x: Partial<Choix>) => {
    setChoix((c) => ({ ...c, [cle]: { ...c[cle], ...x } }));
    setErreurs((e) => { if (!e[cle]) return e; const n = { ...e }; delete n[cle]; return n; });
  };
  const toutes = (liste: Item[], coche: boolean) => setChoix((c) => {
    const n = { ...c };
    for (const i of liste) if (!i.deja) n[i.cle] = { ...n[i.cle], coche };
    return n;
  });

  const ajouter = async () => {
    // Contrôle avant envoi : une quantité (et une unité pour une ligne simple) pour chaque élément coché — jamais 1 d'office.
    const manquants: Record<string, string> = {};
    for (const i of coches) {
      const c = choix[i.cle];
      const q = versNombre(c.quantite);
      if (q === null || q <= 0) manquants[i.cle] = c.quantite.trim() === '' ? 'Quantité à saisir.' : 'Quantité invalide (exemple : 12,5).';
      else if (!c.ouvrageId && !c.unite.trim()) manquants[i.cle] = 'Unité à saisir.';
    }
    if (Object.keys(manquants).length) {
      setErreurs(manquants);
      const premier = coches.find((i) => manquants[i.cle])!;
      refs.current[premier.cle]?.scrollIntoView({ block: 'center' });
      refs.current[premier.cle]?.focus();
      notifier(`${pluriel(Object.keys(manquants).length, 'élément coché', 'éléments cochés')} sans quantité ou sans unité : saisissez-la avant d’ajouter.`, true);
      return;
    }
    setEnvoi(true);
    try {
      const x = await api.post<{ ajoutes: number; lignes: number; deja: number }>(`/api/devis/revisions/${p.revision.id}/proposition/ajouter`, {
        items: coches.map((i) => {
          const c = choix[i.cle];
          return { cle: i.cle, ouvrageId: c.ouvrageId || null, quantite: c.quantite.trim(), ...(c.ouvrageId ? {} : { unite: c.unite.trim() }) };
        }),
        ...(taux ? { tauxHoraire: taux } : {}),
      });
      notifier(`${pluriel(x.ajoutes, 'élément ajouté', 'éléments ajoutés')} au devis (${pluriel(x.lignes, 'ligne', 'lignes')})${x.deja ? `, ${x.deja} déjà présent(s)` : ''}.`);
      await Promise.all([qc.invalidateQueries({ queryKey: ['proposition-chiffrage', p.revision.id] }), qc.invalidateQueries({ queryKey: ['revision', p.revision.id] }),
        qc.invalidateQueries({ queryKey: ['devis', p.devis.id] })]);
    } catch (e) {
      notifier(messageErreur(e), true);
    } finally { setEnvoi(false); }
  };

  const cadres = p.pieces.cadres.map((c) => `« ${c.titre} »`).join(', ');
  return (
    <div className="pile-l pc-page">
      <header className="pc-entete">
        <h2 className="devis-titre" style={{ margin: 0 }}>Chiffrage proposé — <span className="mono">{p.devis.number}</span></h2>
        <p className="petit secondaire" style={{ margin: 0 }}>
          {p.devis.title} · {p.revision.variant}, révision {p.revision.rev_no}
          <span className="expert-seulement">{cadres ? ` · cadre : ${cadres}` : ' · aucun DPGF ni bordereau'}{p.pieces.comptages_cctp ? ` · ${pluriel(p.pieces.comptages_cctp, 'quantité lue', 'quantités lues')} dans le CCTP` : ''}{p.pieces.metres ? ` · ${pluriel(p.pieces.metres, 'métré de plan', 'métrés de plan')}` : ''}</span>
        </p>
      </header>
      {lecture && (
        <Alerte ton="info" titre={p.droits.ecrire ? 'Révision non modifiable' : 'Lecture seule'}>
          {p.droits.ecrire ? 'Cette révision n’est plus en brouillon : la proposition se consulte, elle ne peut pas y être ajoutée.' : 'Vos droits permettent de consulter la proposition, pas de l’ajouter au devis.'}
        </Alerte>
      )}
      <p className="secondaire pc-intro" style={{ margin: 0 }}>
        Le DPGF fait foi sur les quantités ; sinon le comptage sur plan validé, puis le nombre écrit dans le CCTP, puis le métré. Les écarts restent à lever. Rien n’est ajouté tant que vous n’avez pas cliqué « Ajouter au devis ».
      </p>
      <ul className="pc-resume" aria-label="Résumé de la proposition">
        <li><strong className="chiffre">{p.resume.cadre}</strong> {p.resume.cadre > 1 ? 'lignes du cadre' : 'ligne du cadre'}</li>
        <li><strong className="chiffre">{p.resume.exigences}</strong> {p.resume.exigences > 1 ? 'exigences sans ligne au cadre' : 'exigence sans ligne au cadre'}</li>
        {p.resume.ecarts > 0 && <li className="pc-resume-ambre"><AlertTriangle aria-hidden /><strong className="chiffre">{p.resume.ecarts}</strong> écart{p.resume.ecarts > 1 ? 's' : ''} entre sources</li>}
        {comptages.length > 0 && <li>{comptages.length} ouvrage{comptages.length > 1 ? 's' : ''} compté{comptages.length > 1 ? 's' : ''} sur plan</li>}
        {p.resume.quantite_a_saisir > 0 && <li className="pc-resume-ambre"><strong className="chiffre">{p.resume.quantite_a_saisir}</strong> quantité{p.resume.quantite_a_saisir > 1 ? 's' : ''} à saisir</li>}
        {p.resume.deja > 0 && <li><strong className="chiffre">{p.resume.deja}</strong> déjà au devis</li>}
      </ul>
      {!!p.pieces.reperes_a_valider && <Alerte ton="ambre">{p.pieces.reperes_a_valider} repères à valider. Le comptage des ouvrages concernés n’est pas repris, pour ne pas présenter un sous-total comme une quantité complète.</Alerte>}

      {p.droits.couts && !lecture && p.taux_horaires.length > 1 && (
        <label className="champ pc-taux">
          <span className="champ-libelle">Taux horaire des heures des ouvrages ajoutés</span>
          <select className="saisie" value={taux} onChange={(e) => setTaux(e.target.value)}>
            <option value="">Celui de chaque ouvrage (sinon « taux à choisir » sur la ligne)</option>
            {p.taux_horaires.map((t) => <option key={t.code} value={t.code}>{`${t.code} — ${t.label}`}</option>)}
          </select>
        </label>
      )}
      {!p.items.length && (
        <EtatVide titre="Rien à proposer" texte="Le dossier ne contient ni DPGF ou bordereau lisible, ni exigence du CCTP à chiffrer. Déposez les pièces et lancez l’analyse du dossier." />
      )}
      {[{ titre: 'Lignes du DPGF ou du bordereau', liste: cadre }, { titre: 'Comptages sur plan validés', liste: comptages }, { titre: 'Exigences du CCTP sans ligne au DPGF', liste: exigences }].filter((g) => g.liste.length).map((g) => {
        const libres = g.liste.filter((i) => !i.deja);
        const tout = libres.length > 0 && libres.every((i) => choix[i.cle]?.coche);
        return (
          <Panneau key={g.titre} serre titre={`${g.titre} (${g.liste.length})`}
            actions={!lecture && libres.length > 1 ? <Bouton onClick={() => toutes(libres, !tout)}>{tout ? 'Tout décocher' : 'Tout cocher'}</Bouton> : undefined}>
            <ol className="pc-liste">
              {g.liste.map((i) => (
                <CarteItem key={i.cle} i={i} c={choix[i.cle] ?? choixInitial(i)} changer={(x) => changer(i.cle, x)} lecture={lecture}
                  catalogue={catalogue} tous={p.catalogue} couts={p.droits.couts} seuil={p.seuil_mois} erreur={erreurs[i.cle]}
                  refQuantite={(el) => { refs.current[i.cle] = el; }} />
              ))}
            </ol>
          </Panneau>
        );
      })}

      <div className="barre-actions barre-collante pc-barre" role="region" aria-label="Ajouter au devis">
        <span className="petit chiffre barre-collante-resume">
          {lecture ? 'Lecture seule' : `${pluriel(coches.length, 'élément coché', 'éléments cochés')}${sansQuantite ? ` · ${sansQuantite} sans quantité` : ''}`}
        </span>
        {lecture
          ? <LienBouton variante="principal" to={retour} icone={<ArrowLeft aria-hidden />}>Revenir au devis</LienBouton>
          : <Bouton variante="principal" icone={<ListPlus aria-hidden />} chargement={envoi} disabled={!coches.length} onClick={ajouter}>Ajouter au devis</Bouton>}
      </div>
      <p className="petit discret" style={{ margin: 0 }}><Link to={retour}>Revenir au devis sans rien ajouter</Link></p>
    </div>
  );
}

function CarteItem({ i, c, changer, lecture, catalogue, tous, couts, seuil, erreur, refQuantite }: {
  i: Item; c: Choix; changer: (x: Partial<Choix>) => void; lecture: boolean; catalogue: Map<string, OuvrageCatalogue>; tous: OuvrageCatalogue[];
  couts: boolean; seuil: number | null; erreur?: string; refQuantite: (el: HTMLInputElement | null) => void;
}) {
  const o = c.ouvrageId ? catalogue.get(c.ouvrageId) ?? null : null;
  const conf = CONFIANCE[i.confiance];
  const q = versNombre(c.quantite);
  const proposee = i.quantite !== null && q !== null && q === Number(i.quantite) && (!o || o.unit === i.unite);
  const ia = i.exigence?.detection === 'ia' || i.exigencesCouvertes.some((x) => x.detection === 'ia');
  const idCorps = `pc-${i.cle.replace(/[^a-z0-9]/gi, '')}`;
  const autres = tous.filter((x) => x.id !== i.ouvrage?.id && !i.alternatives.some((a) => a.id === x.id));
  const grouper = (m: string | null) => autres.filter((x) => x.metier === m);
  const sourceDoc = i.cadre?.source ?? i.exigence?.source ?? null;
  return (
    <li className={`pc-item${c.coche && !i.deja ? ' est-coche' : ''}${i.deja ? ' pc-deja' : ''}`}>
      <label className="pc-case">
        <input type="checkbox" checked={!i.deja && c.coche} disabled={lecture || i.deja} onChange={(e) => changer({ coche: e.target.checked })} aria-describedby={idCorps} />
        <span className="pc-titre">{i.cadre?.ref ? <span className="mono pc-ref">{i.cadre.ref}</span> : null}{i.libelle}</span>
        {i.deja ? <Badge ton="info">Déjà au devis</Badge> : <Badge ton={conf.ton} icone={false}>{conf.libelle}</Badge>}
        {i.ecart && <Badge ton="danger" icone={<AlertTriangle size={14} aria-hidden />}>{i.ecart.message.includes('Écart comptage') ? 'Écart comptage ↔ pièces' : 'Écart CCTP ↔ DPGF'}</Badge>}
      </label>
      <div className="pc-corps" id={idCorps}>
        {ia && <MarqueIa texte="Exigence proposée par l’assistance IA — à vérifier dans la pièce" />}
        {i.ecart && <p className="pc-ecart petit">{i.ecart.message}</p>}
        <div className="pc-champs">
          <label className="champ pc-ouvrage">
            <span className="champ-libelle">Ouvrage type</span>
            <select className="saisie" value={c.ouvrageId} disabled={lecture || i.deja}
              onChange={(e) => { const n = e.target.value ? catalogue.get(e.target.value) ?? null : null; changer({ ouvrageId: e.target.value, unite: n ? n.unit : (i.cadre?.unite ?? '') }); }}>
              <option value="">Aucun ouvrage (ligne simple)</option>
              {i.ouvrage && <option value={i.ouvrage.id}>{`★ ${i.ouvrage.label} (${i.ouvrage.code})`}</option>}
              {i.alternatives.length > 0 && <optgroup label="Autres ouvrages reconnus">{i.alternatives.map((a) => <option key={a.id} value={a.id}>{`${a.label} (${a.code})`}</option>)}</optgroup>}
              {(['cvc', 'gtb', 'elec', null] as const).map((m) => grouper(m).length > 0 && (
                <optgroup key={m ?? 'autres'} label={m ? METIERS[m] : 'Autres ouvrages'}>
                  {grouper(m).map((x) => <option key={x.id} value={x.id}>{`${x.label} (${x.code})`}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <div className="pc-quantite">
            <label className="champ">
              <span className="champ-libelle">Quantité</span>
              <span className="pc-saisie-unite">
                <input ref={refQuantite} className="saisie pc-saisie-q" inputMode="decimal" value={c.quantite} placeholder="À saisir" disabled={lecture || i.deja}
                  aria-invalid={!!erreur || undefined} onChange={(e) => changer({ quantite: e.target.value })} />
                {o ? <span className="pc-unite">{o.unit}</span> : (
                  <input className="saisie pc-saisie-u" aria-label="Unité" value={c.unite} placeholder="unité" disabled={lecture || i.deja} maxLength={20}
                    onChange={(e) => changer({ unite: e.target.value })} />
                )}
              </span>
            </label>
            {erreur && <span className="champ-erreur">{erreur}</span>}
          </div>
        </div>
        <p className={`petit pc-source${i.sourceQuantite.type === 'aucune' && q === null ? ' pc-a-saisir' : ''}`}>
          {proposee || q === null
            ? <>{i.sourceQuantite.type === 'aucune' ? null : <strong>Source : </strong>}{i.sourceQuantite.libelle}</>
            : <><strong>Saisie à la main</strong>{i.quantite !== null ? ` — proposé : ${quantite(i.quantite)} ${i.unite ?? ''} (${i.sourceQuantite.libelle})` : ''}</>}
          {i.sourceQuantite.source?.versionId && (
            <span className="pc-lien-source"> <Source versionId={i.sourceQuantite.source.versionId} page={i.sourceQuantite.source.page} titre="Voir la pièce"
              extrait={i.sourceQuantite.source.excerpt?.slice(0, 120) ?? undefined} /></span>
          )}
        </p>
        {!lecture && !i.deja && i.suggestions.length > 0 && (
          <div className="pc-suggestions">
            {i.suggestions.map((s) => (
              <Bouton key={`${s.quantite}-${s.libelle}`} compact title={s.libelle} onClick={() => changer({ quantite: versSaisie(s.quantite) })}>
                {`Prendre ${quantite(s.quantite)} ${s.unite}`}
              </Bouton>
            ))}
            <span className="tres-petit discret pc-suggestions-texte">{i.suggestions.map((s) => `${quantite(s.quantite)} : ${s.libelle}`).join(' · ')}</span>
          </div>
        )}
        <p className="petit secondaire pc-explication">{i.explication}</p>
        {i.avertissements.length > 0 && (
          <ul className="pc-avertissements petit">{i.avertissements.map((a) => <li key={a}>{a}</li>)}</ul>
        )}
        {i.exigencesCouvertes.length > 0 && (
          <p className="tres-petit discret expert-seulement" style={{ margin: 0 }}>
            Exigence{i.exigencesCouvertes.length > 1 ? 's' : ''} du CCTP couverte{i.exigencesCouvertes.length > 1 ? 's' : ''} par cette ligne : {i.exigencesCouvertes.map((x) => `« ${x.label} »`).join(', ')}
          </p>
        )}
        {sourceDoc?.versionId && i.origine === 'exigence' && (
          <span className="tres-petit expert-seulement"><Source versionId={sourceDoc.versionId} page={sourceDoc.page} titre={sourceDoc.titre} localisation={sourceDoc.locator}
            extrait={sourceDoc.excerpt?.slice(0, 120) ?? undefined} /></span>
        )}
        {o && <Detail o={o} q={q} couts={couts} seuil={seuil} />}
      </div>
    </li>
  );
}

/** Heures et matériel de l'ouvrage pour la quantité saisie (base seulement) ; heures et prix : rôles qui voient les coûts. */
function Detail({ o, q, couts, seuil }: { o: OuvrageCatalogue; q: number | null; couts: boolean; seuil: number | null }) {
  const base = o.composants.filter((x) => x.role === 'base');
  const penser = o.composants.filter((x) => x.role === 'penser_a_tout');
  const heures = couts && o.heures && q !== null && o.heures.par_unite !== null && o.heures.fixes !== null
    ? Number(o.heures.par_unite) * q + Number(o.heures.fixes) : null;
  return (
    <details className="pc-detail expert-seulement">
      <summary className="petit">
        {couts
          ? <>Heures et matériel{q === null ? ' (saisissez la quantité)' : heures !== null ? ` — ${h2(heures)} h de pose` : ' — temps de pose à renseigner'}</>
          : <>Matériel de l’ouvrage <Lock size={13} aria-label="heures et prix réservés" /></>}
      </summary>
      <ul className="pc-composants petit">
        {base.map((x) => {
          const qte = q === null ? null : (x.per_unit ? Number(x.qty_per_unit) * q : Number(x.qty_per_unit));
          return (
            <li key={x.id}>
              <span className="pc-comp-lib">{x.label}<span className="discret"> — {LIBELLES_SECTIONS[x.section]}</span></span>
              <span className="chiffre">{qte === null ? `${quantite(x.qty_per_unit)} ${x.unit} par ${o.unit}` : `${h2(qte)} ${x.unit}`}</span>
              {couts && x.fourniture && <PrixComposant t={x.tarif ?? null} reference={x.reference} seuil={seuil} />}
              {couts && !x.fourniture && <span className="tres-petit discret">main-d’œuvre seule{x.heures === null ? ' — temps à renseigner' : ''}</span>}
            </li>
          );
        })}
      </ul>
      {penser.length > 0 && <p className="tres-petit discret" style={{ margin: 0 }}>À prévoir aussi (non ajouté, à chiffrer si besoin) : {penser.map((x) => x.label).join(', ')}.</p>}
      {couts && o.estimation && <p className="tres-petit discret" style={{ margin: 0 }}><Sparkles size={12} aria-hidden /> Temps estimés, à valider par l’entreprise ({o.origine_temps}).</p>}
    </details>
  );
}

function PrixComposant({ t, reference, seuil }: { t: Tarif | null; reference: string | null; seuil: number | null }) {
  if (!t || t.etat !== 'prix') {
    const pourquoi = !t || t.etat === 'sans_reference' ? 'aucune référence au catalogue' : t.etat === 'introuvable' ? `référence ${reference} absente du catalogue` : `tarif de ${reference} sans prix`;
    return <span className="tres-petit devis-motif">Prix à saisir ({pourquoi})</span>;
  }
  return (
    <span className="tres-petit">
      <span className="chiffre">{euros(t.prix)}</span> <span className="discret">— {t.source}{t.date ? `, ${date(t.date)}` : ', date inconnue'}</span>
      {t.ancien && <Badge ton="ambre" titre={seuil ? `Prix daté de plus de ${seuil} mois` : undefined}>{t.age_mois !== null && t.age_mois !== undefined ? `Prix de ${t.age_mois} mois` : 'Prix non daté'}</Badge>}
    </span>
  );
}
